/**
 * Kenzu Gallery
 * Shopify 2.0
 */

(() => {
  "use strict";

  const SECTION_SELECTOR = "[data-kenzu-gallery]";
  const instances = new WeakMap();

  class KenzuGallery {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector("[data-gallery-track]");
      this.items = Array.from(
        section.querySelectorAll("[data-gallery-item]")
      );

      this.prevButton = section.querySelector("[data-gallery-prev]");
      this.nextButton = section.querySelector("[data-gallery-next]");
      this.pagination = section.querySelector("[data-gallery-pagination]");

      this.lightbox = section.querySelector("[data-gallery-lightbox]");
      this.lightboxImage = section.querySelector(
        "[data-gallery-lightbox-image]"
      );
      this.lightboxClose = section.querySelector(
        "[data-gallery-lightbox-close]"
      );

      this.hostedVideos = Array.from(
        section.querySelectorAll("video")
      );

      this.externalVideos = Array.from(
        section.querySelectorAll(
          "[data-gallery-external-video]"
        )
      );

      this.abortController = new AbortController();
      this.revealObserver = null;
      this.videoObserver = null;
      this.externalObservers = [];
      this.currentIndex = 0;
      this.lastFocusedElement = null;
      this.scrollFrame = null;

      this.init();
    }

    init() {
      this.initializeReveal();
      this.initializeSlider();
      this.initializeHostedVideos();
      this.initializeExternalVideos();
      this.initializeLightbox();

      document.addEventListener(
        "visibilitychange",
        () => this.handleVisibilityChange(),
        {
          signal: this.abortController.signal
        }
      );

      window.addEventListener(
        "resize",
        () => this.handleResize(),
        {
          passive: true,
          signal: this.abortController.signal
        }
      );
    }

    initializeReveal() {
      const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      if (
        reducedMotion ||
        !("IntersectionObserver" in window)
      ) {
        this.section.classList.add("is-visible");
        return;
      }

      this.revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            this.section.classList.add("is-visible");
            observer.unobserve(entry.target);
          });
        },
        {
          threshold: 0.1,
          rootMargin: "0px 0px -8% 0px"
        }
      );

      this.revealObserver.observe(this.section);
    }

    initializeSlider() {
      if (
        !this.track ||
        !this.items.length ||
        !this.section.classList.contains(
          "kenzu-gallery--mobile-slider"
        )
      ) {
        return;
      }

      this.createPagination();
      this.updateSliderState();

      this.prevButton?.addEventListener(
        "click",
        () => this.goToSlide(this.currentIndex - 1),
        {
          signal: this.abortController.signal
        }
      );

      this.nextButton?.addEventListener(
        "click",
        () => this.goToSlide(this.currentIndex + 1),
        {
          signal: this.abortController.signal
        }
      );

      this.track.addEventListener(
        "scroll",
        () => {
          if (this.scrollFrame) {
            cancelAnimationFrame(this.scrollFrame);
          }

          this.scrollFrame = requestAnimationFrame(() => {
            this.updateCurrentSlideFromScroll();
          });
        },
        {
          passive: true,
          signal: this.abortController.signal
        }
      );
    }

    createPagination() {
      if (!this.pagination) return;

      this.pagination.innerHTML = "";

      this.items.forEach((_, index) => {
        const dot = document.createElement("span");

        dot.className = "kenzu-gallery__dot";
        dot.dataset.galleryDot = String(index);

        if (index === 0) {
          dot.classList.add("is-active");
        }

        this.pagination.appendChild(dot);
      });
    }

    goToSlide(index) {
      if (!this.track || !this.items.length) return;

      const safeIndex = Math.max(
        0,
        Math.min(index, this.items.length - 1)
      );

      const target = this.items[safeIndex];

      target.scrollIntoView({
        behavior: window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches
          ? "auto"
          : "smooth",
        block: "nearest",
        inline: "start"
      });

      this.currentIndex = safeIndex;
      this.updateSliderState();
    }

    updateCurrentSlideFromScroll() {
      if (!this.track || !this.items.length) return;

      const trackRect = this.track.getBoundingClientRect();
      let closestIndex = 0;
      let closestDistance = Infinity;

      this.items.forEach((item, index) => {
        const itemRect = item.getBoundingClientRect();
        const distance = Math.abs(
          itemRect.left - trackRect.left
        );

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== this.currentIndex) {
        this.currentIndex = closestIndex;
        this.updateSliderState();
      }
    }

    updateSliderState() {
      this.prevButton?.toggleAttribute(
        "disabled",
        this.currentIndex === 0
      );

      this.nextButton?.toggleAttribute(
        "disabled",
        this.currentIndex === this.items.length - 1
      );

      this.pagination
        ?.querySelectorAll("[data-gallery-dot]")
        .forEach((dot, index) => {
          dot.classList.toggle(
            "is-active",
            index === this.currentIndex
          );
        });
    }

    initializeHostedVideos() {
      if (!this.hostedVideos.length) return;

      this.hostedVideos.forEach((video) => {
        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");

        if (!video.hasAttribute("preload")) {
          video.preload = "metadata";
        }
      });

      if (!("IntersectionObserver" in window)) {
        this.hostedVideos.forEach((video) => {
          if (video.autoplay) {
            this.safePlay(video);
          }
        });

        return;
      }

      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;

            if (!(video instanceof HTMLVideoElement)) {
              return;
            }

            if (
              entry.isIntersecting &&
              video.autoplay &&
              !document.hidden
            ) {
              this.safePlay(video);
            } else {
              video.pause();
            }
          });
        },
        {
          threshold: 0.2,
          rootMargin: "140px 0px"
        }
      );

      this.hostedVideos.forEach((video) => {
        this.videoObserver.observe(video);
      });
    }

    initializeExternalVideos() {
      this.externalVideos.forEach((container) => {
        const playButton = container.querySelector(
          "[data-gallery-play]"
        );

        const autoplay =
          container.dataset.autoplay === "true";

        playButton?.addEventListener(
          "click",
          () => this.loadExternalVideo(container, true),
          {
            signal: this.abortController.signal
          }
        );

        if (autoplay) {
          this.observeExternalVideo(container);
        }
      });
    }

    observeExternalVideo(container) {
      if (!("IntersectionObserver" in window)) {
        this.loadExternalVideo(container, true);
        return;
      }

      const observer = new IntersectionObserver(
        (entries, currentObserver) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            this.loadExternalVideo(container, true);
            currentObserver.disconnect();
          });
        },
        {
          threshold: 0,
          rootMargin: "300px 0px"
        }
      );

      observer.observe(container);
      this.externalObservers.push(observer);
    }

    loadExternalVideo(container, autoplay = false) {
      if (!(container instanceof HTMLElement)) return;

      if (container.dataset.loaded === "true") {
        this.playExternalVideo(container);
        return;
      }

      const type = container.dataset.videoType;
      const id = container.dataset.videoId;
      const loop = container.dataset.loop === "true";
      const controls =
        container.dataset.controls === "true";

      if (!type || !id) return;

      const iframe = document.createElement("iframe");

      iframe.className = "kenzu-gallery__external-iframe";
      iframe.title = "Vídeo de la galería Kenzu";
      iframe.loading = autoplay ? "lazy" : "eager";
      iframe.allow =
        "autoplay; encrypted-media; fullscreen; picture-in-picture";

      iframe.setAttribute("allowfullscreen", "");
      iframe.setAttribute("frameborder", "0");

      if (type === "youtube") {
        iframe.src = this.buildYouTubeUrl({
          id,
          autoplay,
          loop,
          controls
        });
      }

      if (type === "vimeo") {
        iframe.src = this.buildVimeoUrl({
          id,
          autoplay,
          loop,
          controls
        });
      }

      if (!iframe.src) return;

      iframe.addEventListener(
        "load",
        () => {
          container.classList.add("is-playing");
        },
        {
          once: true,
          signal: this.abortController.signal
        }
      );

      container.appendChild(iframe);
      container.dataset.loaded = "true";
      container.classList.add("is-playing");
    }

    buildYouTubeUrl({
      id,
      autoplay,
      loop,
      controls
    }) {
      const params = new URLSearchParams({
        autoplay: autoplay ? "1" : "0",
        mute: "1",
        controls: controls ? "1" : "0",
        loop: loop ? "1" : "0",
        playsinline: "1",
        rel: "0",
        modestbranding: "1",
        enablejsapi: "1",
        origin: window.location.origin
      });

      if (loop) {
        params.set("playlist", id);
      }

      return `https://www.youtube.com/embed/${encodeURIComponent(
        id
      )}?${params.toString()}`;
    }

    buildVimeoUrl({
      id,
      autoplay,
      loop,
      controls
    }) {
      const params = new URLSearchParams({
        autoplay: autoplay ? "1" : "0",
        muted: "1",
        loop: loop ? "1" : "0",
        controls: controls ? "1" : "0",
        playsinline: "1",
        autopause: "0",
        dnt: "1"
      });

      if (autoplay && !controls) {
        params.set("background", "1");
      }

      return `https://player.vimeo.com/video/${encodeURIComponent(
        id
      )}?${params.toString()}`;
    }

    playExternalVideo(container) {
      const iframe = container.querySelector("iframe");

      if (!(iframe instanceof HTMLIFrameElement)) return;

      const type = container.dataset.videoType;

      if (type === "youtube") {
        iframe.contentWindow?.postMessage(
          JSON.stringify({
            event: "command",
            func: "playVideo",
            args: []
          }),
          "*"
        );
      }

      if (type === "vimeo") {
        iframe.contentWindow?.postMessage(
          {
            method: "play"
          },
          "*"
        );
      }

      container.classList.add("is-playing");
    }

    pauseExternalVideo(container) {
      const iframe = container.querySelector("iframe");

      if (!(iframe instanceof HTMLIFrameElement)) return;

      const type = container.dataset.videoType;

      if (type === "youtube") {
        iframe.contentWindow?.postMessage(
          JSON.stringify({
            event: "command",
            func: "pauseVideo",
            args: []
          }),
          "*"
        );
      }

      if (type === "vimeo") {
        iframe.contentWindow?.postMessage(
          {
            method: "pause"
          },
          "*"
        );
      }
    }

    initializeLightbox() {
      const triggers = Array.from(
        this.section.querySelectorAll(
          "[data-gallery-lightbox-trigger]"
        )
      );

      if (
        !this.lightbox ||
        !this.lightboxImage ||
        !triggers.length
      ) {
        return;
      }

      triggers.forEach((trigger) => {
        trigger.addEventListener(
          "click",
          (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.openLightbox(trigger);
          },
          {
            signal: this.abortController.signal
          }
        );
      });

      this.lightboxClose?.addEventListener(
        "click",
        () => this.closeLightbox(),
        {
          signal: this.abortController.signal
        }
      );

      this.lightbox.addEventListener(
        "click",
        (event) => {
          if (event.target === this.lightbox) {
            this.closeLightbox();
          }
        },
        {
          signal: this.abortController.signal
        }
      );

      document.addEventListener(
        "keydown",
        (event) => {
          if (
            event.key === "Escape" &&
            this.lightbox?.classList.contains("is-open")
          ) {
            this.closeLightbox();
          }

          if (
            event.key === "Tab" &&
            this.lightbox?.classList.contains("is-open")
          ) {
            this.trapLightboxFocus(event);
          }
        },
        {
          signal: this.abortController.signal
        }
      );
    }

    openLightbox(trigger) {
      const imageUrl = trigger.dataset.fullImage;
      const imageAlt = trigger.dataset.imageAlt || "";

      if (
        !imageUrl ||
        !this.lightbox ||
        !this.lightboxImage
      ) {
        return;
      }

      this.lastFocusedElement = document.activeElement;

      this.lightboxImage.src = imageUrl;
      this.lightboxImage.alt = imageAlt;

      this.lightbox.classList.add("is-open");
      this.lightbox.setAttribute("aria-hidden", "false");

      document.body.classList.add(
        "kenzu-gallery-lightbox-open"
      );

      this.lightboxClose?.focus();
    }

    closeLightbox() {
      if (!this.lightbox || !this.lightboxImage) return;

      this.lightbox.classList.remove("is-open");
      this.lightbox.setAttribute("aria-hidden", "true");

      document.body.classList.remove(
        "kenzu-gallery-lightbox-open"
      );

      window.setTimeout(() => {
        if (
          !this.lightbox?.classList.contains("is-open")
        ) {
          this.lightboxImage.src = "";
          this.lightboxImage.alt = "";
        }
      }, 250);

      if (
        this.lastFocusedElement instanceof HTMLElement
      ) {
        this.lastFocusedElement.focus();
      }
    }

    trapLightboxFocus(event) {
      if (!this.lightbox) return;

      const focusable = Array.from(
        this.lightbox.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first.focus();
      }
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.pauseAllVideos();
        return;
      }

      this.hostedVideos.forEach((video) => {
        if (
          video.autoplay &&
          this.isElementVisible(video)
        ) {
          this.safePlay(video);
        }
      });
    }

    handleResize() {
      if (window.innerWidth >= 990) {
        this.currentIndex = 0;
        this.updateSliderState();
      } else {
        this.updateCurrentSlideFromScroll();
      }
    }

    pauseAllVideos() {
      this.hostedVideos.forEach((video) => {
        video.pause();
      });

      this.externalVideos.forEach((container) => {
        this.pauseExternalVideo(container);
      });
    }

    safePlay(video) {
      if (!(video instanceof HTMLVideoElement)) return;

      const promise = video.play();

      promise?.catch?.(() => {});
    }

    isElementVisible(element) {
      if (!(element instanceof Element)) return false;

      const rect = element.getBoundingClientRect();

      return (
        rect.bottom > 0 &&
        rect.right > 0 &&
        rect.top < window.innerHeight &&
        rect.left < window.innerWidth
      );
    }

    destroy() {
      this.pauseAllVideos();
      this.closeLightbox();

      this.abortController.abort();
      this.revealObserver?.disconnect();
      this.videoObserver?.disconnect();

      this.externalObservers.forEach((observer) => {
        observer.disconnect();
      });

      if (this.scrollFrame) {
        cancelAnimationFrame(this.scrollFrame);
      }

      this.externalObservers = [];
    }
  }

  const initializeSection = (section) => {
    if (!(section instanceof HTMLElement)) return;
    if (instances.has(section)) return;

    instances.set(section, new KenzuGallery(section));
  };

  const initializeSections = (root = document) => {
    if (
      root instanceof HTMLElement &&
      root.matches(SECTION_SELECTOR)
    ) {
      initializeSection(root);
    }

    root
      .querySelectorAll?.(SECTION_SELECTOR)
      .forEach(initializeSection);
  };

  const destroySections = (root) => {
    const sections = [];

    if (
      root instanceof HTMLElement &&
      root.matches(SECTION_SELECTOR)
    ) {
      sections.push(root);
    }

    root
      .querySelectorAll?.(SECTION_SELECTOR)
      .forEach((section) => {
        sections.push(section);
      });

    sections.forEach((section) => {
      instances.get(section)?.destroy();
      instances.delete(section);
    });
  };

  const boot = () => {
    initializeSections(document);
  };

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      boot,
      {
        once: true
      }
    );
  } else {
    boot();
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeSections(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      destroySections(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:select",
    (event) => {
      const section =
        event.target.matches?.(SECTION_SELECTOR)
          ? event.target
          : event.target.querySelector?.(
              SECTION_SELECTOR
            );

      section?.classList.add("is-visible");
    }
  );

  document.addEventListener(
    "shopify:block:select",
    (event) => {
      const item = event.target.closest?.(
        "[data-gallery-item]"
      );

      const section = item?.closest(
        SECTION_SELECTOR
      );

      section?.classList.add("is-visible");

      item?.scrollIntoView({
        behavior: window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches
          ? "auto"
          : "smooth",
        block: "center",
        inline: "center"
      });
    }
  );
})();